import { Navigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/hooks/use-role";

// A-08: server-side role verification on mount.
// Calls get-user-role Edge Function (reads from DB, not client cache) to confirm
// the user still has superadmin access. Protects against stale cache after role revocation.
function useServerRoleCheck() {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["server-role-check-superadmin"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("get-user-role");
      if (error) throw error;
      const serverRole: string = data?.role || "user";
      const hasAccess = serverRole === "superadmin";
      if (!hasAccess) {
        queryClient.invalidateQueries({ queryKey: ["user-role"] });
      }
      return { serverRole, hasAccess };
    },
    staleTime: 0,
    retry: 1,
  });
}

interface SuperAdminRouteProps {
  children: React.ReactNode;
}

export default function SuperAdminRoute({ children }: SuperAdminRouteProps) {
  const { hasSuperAdminAccess, loading: roleLoading } = useRole();
  const { data: serverCheck, isLoading: serverLoading } = useServerRoleCheck();

  if (roleLoading || serverLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (!hasSuperAdminAccess || serverCheck?.hasAccess === false) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
